import angular from 'angular';

function fieldName(field) {
  return field.split(':')[1];
}

function fieldWhere(field) {
  return field.split(':')[0];
}

/**
 * Splitted from metric.controller.js, to lighten it (~2800 lines before split)
 * We can probably do better when migrating in angular, for now it's not refactoring, just splitting.
 * Create could be in constructor, dependencies injection, extend abstract class, remove self, remove bind(this)...
 */
export class RadioSelector {
  // useless declarations in JS (not in TS), just to see class members at a glance
  company;
  metric;
  LocalStorageFactory;
  MetricsFactory;

  config;
  fields;
  key;
  self;
  isWithEmptyOption;

  constructor(company, metric, LocalStorageFactory, MetricsFactory) {
    this.company = company;
    this.metric = metric;
    this.LocalStorageFactory = LocalStorageFactory;
    this.MetricsFactory = MetricsFactory;
  }

  create(fields, name, items, isMandatory, isWithEmptyOption, displayedByDefault) {
    this.fields = fields;
    this.isWithEmptyOption = isWithEmptyOption;

    this.key = `3-company-${this.company.id}-metric-${this.metric.id}-field-${fields.join('-')}${name}`;

    this.self = {
      selected: null,
      active: null,
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        is_active_class: item.is_active_class ? `active-icon-${item.is_active_class}` : '',
        icon_name: item.site_type ? `task-icon-${item.site_type}` : item.site_type,
      })),
      apply: this.apply.bind(this),
      name: name,
      isDisplayed: displayedByDefault === undefined ? true : displayedByDefault, // (almost) all radio selectors mandated by Metrics.py are to be displayed by default (unless explicitely specified)
      isMandatory: isMandatory,
      isWithEmptyOption: isWithEmptyOption,
      type: 'radio',
      remove: this.remove.bind(this),
      hasValue: this.hasValue.bind(this),
      selectedCriteriaForXls: this.selectedCriteriaForXls.bind(this),
      query: this.query.bind(this),
      load: this.load.bind(this),
      save: this.save.bind(this),
      loadFromReport: this.loadFromReport.bind(this),
    };
    return this.self;
  }

  remove() {
    this.self.selected = null;
  }

  hasValue() {
    return !!this.self.selected;
  }

  selectedCriteriaForXls() {
    return {
      name: this.self.name,
      // selectedCriteriaForXls() is only called on selector that have a value, but better safe than sorry
      values: this.self.selected ? [{ name: this.self.selected.name }] : [],
    };
  }

  query() {
    let retval = {};
    for (let field of this.fields) {
      // query() is only called on selectors that have a value, but better safe than sorry
      const rvalue = this.self.selected ? this.self.selected.id : null;

      // Scenario-selectors' item values are arrays of graph element ids; the SQL needs an 'in' operator in that case
      // Otherwise we use an '=' operator
      let op;
      if (Array.isArray(rvalue)) {
        op = 'in';
      } else {
        if (['null', 'not null'].includes(rvalue)) {
          op = 'is';
        } else {
          op = '=';
        }
      }
      retval[fieldWhere(field)] = [
        [
          {
            op: op,
            lvalue: fieldName(field),
            rvalue: rvalue,
          },
        ],
      ];
    }
    return retval;
  }

  load() {
    let defaultValue = this.isWithEmptyOption ? null : this.self.items[0];
    let value = this.LocalStorageFactory.getItem(this.key);
    if (value !== null) {
      // filter out elements that might have disappeared from the QR graph
      // since last we saved this selector to local storage
      let selected = this.self.items.filter(i => angular.equals(value, i.id));
      if (selected.length > 0) {
        this.self.selected = selected[0];
      } else {
        this.self.selected = defaultValue;
      }
    } else {
      this.self.selected = defaultValue;
    }
  }

  loadFromReport(selectedReport, reportDate) {
    if (selectedReport) {
      let filters = [];
      Object.keys(selectedReport.filters).forEach(key => {
        filters = [...filters, ...selectedReport.filters[key]];
      });
      if (filters.length) {
        let candidateValues = [];
        filters.forEach(selector => {
          selector.forEach(clause => {
            let single_clause_value = clause[0];
            if (this.fields[0].includes(single_clause_value.lvalue)) {
              candidateValues.push(single_clause_value.rvalue);
            }
          });
        });
        // Filter out elements that might have been removed from a qr graph since report creation,
        // and select the correct value candidate
        // (on page qr-clicks, both scenario and scenario messsage selectors
        //  have "scenario_button" as an lvalue)
        let selected = this.self.items.filter(
          item => candidateValues.findIndex(v => angular.equals(v, item.id)) !== -1
        );
        if (selected.length > 0) {
          this.self.selected = selected[0];
        } else {
          this.self.selected = this.isWithEmptyOption ? null : this.self.items[0];
        }
      }
    }
  }

  save() {
    const value = this.self.selected ? this.self.selected.id : null;
    this.LocalStorageFactory.setItem(this.key, value);
  }

  apply() {
    this.submitQuery();
  }

  submitQuery() {
    this.MetricsFactory.queriesWithThrottleTime$.next();
  }
}
